import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ClassSelector,
  CustomFormValidators,
  EcommerceTrackingService,
  RoutingService,
  ScrollService,
  WindowRef,
} from '@boels/helpers';
import {
  CmsComponentData,
  GDPRMessageComponentData,
  GlobalMessageType,
  TrackingFormSubmitStatus,
  TrackingFormType,
} from '@boels/models';
import {
  GlobalMessageFacade,
  QuoteDTO,
  QuoteFacade,
  UserProfileFacade,
} from '@boels/shared-state';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { NgSelectModule } from '@ng-select/ng-select';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  catchError,
  filter,
  first,
  map,
  of,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { FormErrorsComponent } from '../form/form-errors/form-errors.component';
import { TextInputComponent } from '../form/input/text-input.component';
import { TextareaComponent } from '../form/textarea/textarea.component';
import { DatePickerComponent } from '../shopping-cart/date-picker/date-picker.component';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { PrivacyStatementLinkComponent } from '../form/privacy-statement-link/privacy-statement-link.component';
import { QuantityComponent } from '@boels/elements';
import { PhoneComponent } from '../phone/phone.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'boels-quote-form',
  templateUrl: './quote-form.component.html',
  styleUrls: ['./quote-form.component.scss'],
  host: { ngSkipHydration: 'true' },
  imports: [
    TranslocoModule,
    ReactiveFormsModule,
    CommonModule,
    NgSelectModule,
    FormErrorsComponent,
    TextInputComponent,
    PhoneComponent,
    TextareaComponent,
    DatePickerComponent,
    MatInputModule,
    MatDatepickerModule,
    PrivacyStatementLinkComponent,
    QuantityComponent,
    ButtonComponent,
  ],
  providers: [
    QuoteFacade,
    ScrollService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['checkout', 'form'],
    },
  ],
})
export class QuoteFormComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private userService = inject(UserProfileFacade);
  private globalMessageService = inject(GlobalMessageFacade);
  private scrollService = inject(ScrollService);
  private elementRef = inject(ElementRef);
  private winRef = inject(WindowRef);
  private quoteFacade = inject(QuoteFacade);
  private routingService = inject(RoutingService);
  private router = inject(Router);
  private ecommerceTrackingService = inject(EcommerceTrackingService);

  @Input() componentData: CmsComponentData<GDPRMessageComponentData>;
  @ViewChild('submitButton') submitButton: ElementRef<HTMLButtonElement>;

  public countries$ = this.userService.getCountries();
  public titles$ = this.userService.getTitles();

  public startDate: string;
  public productNumber: string;

  public submitted = false;
  public faChevronRightIcon = faChevronRight;
  public numberOfUnitsArr: Array<string> = ['1', '2-4', '5-10', '10-20', '>20'];
  public minDate: Date = new Date();

  public quoteForm: FormGroup = this.fb.group({
    // Project Details part
    estimatedRentalPeriod: [4, [Validators.required, Validators.min(4)]],
    numberOfUnits: [this.numberOfUnitsArr[0], Validators.required],
    projectLocation: ['', Validators.required],
    deliveryDate: ['', Validators.required],
    message: [''],
    // Your Details part
    title: [null, Validators.required],
    firstName: ['', Validators.required],
    surname: ['', Validators.required],
    telephone: ['', Validators.required],
    email: ['', [Validators.required, CustomFormValidators.emailValidator]],
    // Company Details part
    companyName: ['', Validators.required],
    country: [null, Validators.required],
    debtorNumber: [''],
    address: ['', Validators.required],
    postcode: ['', Validators.required],
    city: ['', Validators.required],
  });

  private destroy$ = new Subject<void>();

  public onDebtorInputKeypress = function (event: KeyboardEvent): boolean {
    const input = String.fromCharCode(event.keyCode);

    // allow only alphanumeric values to type in
    if (/[a-zA-Z0-9]/.test(input)) {
      return true;
    }
    event.preventDefault();
    return false;
  };

  get country(): FormControl {
    return this.quoteForm.controls['country'] as FormControl;
  }

  get telephone(): FormControl {
    return this.quoteForm.controls['telephone'] as FormControl;
  }

  ngOnInit(): void {
    this.startDate = this.formatDateAsString(new Date());
    const queryParams = this.routingService.getRouteProperty('queryParams');
    this.productNumber = queryParams?.['product'];

    this.minDate.setDate(this.minDate.getDate() + 1);
    this.initCountry();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onDatesSelected($event: { startDate: Date }): void {
    this.quoteForm.controls['deliveryDate'].setValue(
      this.formatDateAsString($event.startDate),
    );
  }

  public submitForm(): void {
    this.submitted = true;

    if (this.quoteForm.valid) {
      this.sendForm();
    } else {
      this.handleErrors();
    }
  }

  private sendForm() {
    this.quoteFacade
      .submitForm(this.mapValues(), this.productNumber)
      .pipe(
        map(() => ({
          message: {
            key: 'customerContactForm.submitSuccess',
            scope: 'form',
          },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        })),
        tap(() => {
          this.ecommerceTrackingService.setFormSubmitEvent(
            TrackingFormType.QUOTATION,
            TrackingFormSubmitStatus.SUCCESS,
          );
          this.resetForm();
        }),
        catchError(() => {
          this.ecommerceTrackingService.setFormSubmitEvent(
            TrackingFormType.QUOTATION,
            TrackingFormSubmitStatus.FAIL,
          );
          return of({
            message: { key: 'customerContactForm.submitError', scope: 'form' },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        }),
        take(1),
      )
      .subscribe(({ message, type }) => {
        if (type !== GlobalMessageType.MSG_TYPE_ERROR) {
          this.router.navigate([`/siteacc/success`]);
        }
        return this.globalMessageService.add(message, type);
      });

    this.winRef.nativeWindow?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private mapValues(): QuoteDTO {
    return {
      ...this.quoteForm.value,
      telephone: this.telephone.value.e164Number,
    };
  }

  private handleErrors() {
    this.quoteForm.markAllAsTouched();
    this.scrollService.scrollToElement(
      ClassSelector.INVALID_FORM_INPUT,
      this.elementRef,
    );
  }

  private resetForm(): void {
    this.submitted = false;
    this.quoteForm.reset();
    this.initCountry();
  }

  private formatDateAsString(date: Date): string {
    //method toISOString in some cases changes the day because of timezones
    //following line is a workaround to avoid this change from happening
    date = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

    return date
      .toISOString()
      .substring(0, date.toISOString().indexOf('T'))
      .replace('/', '-');
  }

  private initCountry(): void {
    this.countries$
      .pipe(
        takeUntil(this.destroy$),
        map((details) => details.countries),
        filter((countries) => Boolean(countries?.length)),
        first(),
      )
      .subscribe((countries) =>
        this.country.patchValue(countries?.at(0)?.isocode),
      );
  }
}
